/**
 * Billing review > Pre-bill: who still has time to release, the matters
 * with released time, one matter's entries with their flags inline (fix,
 * dismiss or return each), and the rules that check it. A matter approves
 * once nothing on it blocks.
 */
import {
  AlertTriangle,
  ArrowRight,
  Bell,
  Check,
  CircleCheck,
  Clock,
  FileText,
  Info,
  Plus,
  Undo2
} from "lucide-react";
import { useEffect, useMemo, useState, type FC } from "react";
import { Link } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { StatusBadge } from "@/components/ui/status-badge";
import { Switch } from "@/components/ui/switch";
import { Tag } from "@/components/ui/tag";
import { companyPath } from "@/features/companies/routes";
import { cn } from "@/lib/utils";

import { prebillActions, reviewActions, ruleActions } from "../actions";
import { usePrebill, usePrebillMatter, useWaiting } from "../queries";
import { ruleTitle } from "../rules/ruleModel";
import type {
  AppliedRule,
  EntryFlag,
  PrebillMatter,
  PrebillMatterRow,
  TimeEntry
} from "../types";
import { useHeld } from "../useHeld";
import { formatAmount, formatHours } from "../utils";
import {
  blockingNote,
  countsLine,
  filterEntries,
  firstName,
  fixButton,
  fixedTag,
  fixPreview,
  flagSource,
  isBlocking,
  isFlat,
  openFlags,
  waitingText
} from "./prebillModel";
import { ReturnDialog } from "./ReturnDialog";
import { ReviewEntrySheet } from "./ReviewEntrySheet";

type Range = { from?: string; to?: string };

const shortDate = (iso: string) => {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric"
  });
};

const Initials: FC<{ name: string }> = ({ name }) => (
  <span className="inline-flex size-5 shrink-0 items-center justify-center rounded-full bg-fill-2 text-sm font-medium text-fg-2">
    {name
      .split(" ")
      .map((p) => p[0])
      .join("")
      .slice(0, 2)
      .toUpperCase()}
  </span>
);

const WaitingStrip: FC<{ range: Range }> = ({ range }) => {
  const { data: rows = [] } = useWaiting(range);
  const [nudged, setNudged] = useState<string[]>([]);
  return (
    <div
      className="flex min-h-10 flex-wrap items-center gap-2 border-b border-line-2 px-5 py-1.5"
      data-testid="prebill-waiting"
    >
      <span className="inline-flex items-center gap-1.5 text-sm text-fg">
        {rows.length ? (
          <>
            <Clock className="size-3.5 text-warn" /> Waiting on Timekeepers
          </>
        ) : (
          <>
            <CircleCheck className="size-3.5 text-success" /> All Time Released
          </>
        )}
      </span>
      {rows.length ? (
        rows.map((row) => {
          const done = row.nudged_today || nudged.includes(row.user_id);
          return (
            <span
              key={row.user_id}
              className="inline-flex h-7 items-center gap-2 rounded-full bg-elevated pr-1 pl-1 ring-1 ring-line-2"
              data-testid="waiting-chip"
            >
              <Initials name={row.name} />
              <span className="text-sm">
                <span className="font-medium">{row.name}</span>{" "}
                <span className="text-fg-3">· {waitingText(row)}</span>
              </span>
              <Button
                variant="ghost"
                size="xs"
                disabled={done}
                onClick={async () => {
                  if (await prebillActions.nudge(row, range))
                    setNudged((n) => [...n, row.user_id]);
                }}
              >
                {done ? <Check /> : <Bell />}
                {done ? "Nudged" : "Nudge"}
              </Button>
            </span>
          );
        })
      ) : (
        <span className="text-sm text-fg-3">
          Everyone has released their time for these dates.
        </span>
      )}
      <span className="flex-1" />
      <span className="text-sm text-fg-3">
        You only see time after it's released.
      </span>
    </div>
  );
};

const MatterList: FC<{
  rows: PrebillMatterRow[];
  selected: string | null;
  onPick: (id: string) => void;
}> = ({ rows, selected, onPick }) => (
  <div className="flex min-h-0 flex-col overflow-y-auto border-r border-line-2">
    <div className="flex h-10 shrink-0 items-center gap-1.5 px-4 text-base font-medium text-fg">
      Matters{" "}
      <span className="font-normal text-fg-4 tabular-nums">{rows.length}</span>
    </div>
    <div className="flex flex-col gap-0.5 px-1.5 pb-2">
      {rows.map((m) => {
        const flags = m.errors + m.warnings;
        return (
          <button
            key={m.individual_id}
            type="button"
            onClick={() => onPick(m.individual_id)}
            className={cn(
              "flex flex-col gap-0.5 rounded-md px-2.5 py-1.5 text-left transition-colors hover:bg-fill-1",
              m.individual_id === selected && "bg-fill-2 hover:bg-fill-2"
            )}
            data-testid="prebill-matter"
          >
            <span className="flex items-center gap-2">
              <span className="min-w-0 flex-1 truncate text-base text-fg">
                {m.label}
              </span>
              {m.done ? (
                <StatusBadge tone="success" dot>
                  Approved
                </StatusBadge>
              ) : flags ? (
                <StatusBadge
                  tone={m.errors ? "danger" : "warn"}
                  className="min-w-5 justify-center"
                >
                  {flags}
                </StatusBadge>
              ) : null}
            </span>
            <span className="text-sm text-fg-3 tabular-nums">
              {countsLine(m)}
            </span>
          </button>
        );
      })}
    </div>
  </div>
);

const FlagCard: FC<{
  entry: TimeEntry;
  flag: EntryFlag;
  onReturn: () => void;
}> = ({ entry, flag, onReturn }) => {
  const [busy, setBusy] = useState(false);
  const preview = fixPreview(entry, flag);
  const error = flag.severity === "error";
  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    try {
      await fn();
    } finally {
      setBusy(false);
    }
  };
  return (
    <div
      className={cn(
        "flex flex-col gap-2 rounded-lg px-3 py-2.5 ring-1",
        error
          ? "bg-danger-soft ring-danger-line"
          : "bg-warn-soft ring-warn-line"
      )}
      data-testid="prebill-flag"
    >
      <span
        className={cn(
          "flex flex-wrap items-center gap-x-1.5 text-sm font-medium",
          error ? "text-danger" : "text-warn"
        )}
      >
        <AlertTriangle className="size-3.5" />
        <span>{flag.label}</span>
        <span className="font-normal text-fg-3">
          · {flagSource(flag)}
          {flag.section ? ` ${flag.section}` : ""}
        </span>
      </span>
      <span className="text-sm leading-4.5">{flag.message}</span>
      {preview ? (
        <span className="flex flex-col gap-1 text-sm leading-4.5">
          <span className="text-sm font-medium text-fg-3">
            {preview.kind === "narrative"
              ? "Suggested rewrite"
              : "Suggested change"}
          </span>
          {preview.kind === "rate" ? (
            <span className="text-fg-3 tabular-nums">
              Rate <s>{formatAmount(preview.from)}/h</s> →{" "}
              <b className="text-fg">{formatAmount(preview.to)}/h</b> · Amount{" "}
              <s>{formatAmount(preview.amountFrom)}</s> →{" "}
              <b className="text-fg">{formatAmount(preview.amountTo)}</b>
            </span>
          ) : null}
          {preview.kind === "hours" ? (
            <span className="text-fg-3 tabular-nums">
              Hours <s>{formatHours(preview.from)}</s> →{" "}
              <b className="text-fg">{formatHours(preview.to)}</b>
            </span>
          ) : null}
          {preview.kind === "narrative" ? <span>{preview.text}</span> : null}
        </span>
      ) : null}
      <span className="flex items-center gap-1.5">
        {flag.fix ? (
          <Button
            variant="primary"
            size="xs"
            disabled={busy}
            onClick={() => run(() => prebillActions.fix(flag))}
          >
            <Check /> {fixButton(flag)}
          </Button>
        ) : null}
        <Button
          variant="ghost"
          size="xs"
          disabled={busy}
          onClick={() => run(() => prebillActions.dismiss(flag))}
        >
          Dismiss
        </Button>
        <span className="flex-1" />
        <Button variant="ghost" size="xs" disabled={busy} onClick={onReturn}>
          <Undo2 /> Return to {firstName(entry.user_name)}
        </Button>
      </span>
    </div>
  );
};

const EntryRow: FC<{
  entry: TimeEntry;
  flat: boolean;
  onOpen: () => void;
  onReturn: (flag: EntryFlag) => void;
}> = ({ entry, flat, onOpen, onReturn }) => {
  const flags = entry.status === "released" ? openFlags(entry) : [];
  const tag = fixedTag(entry);
  return (
    <div
      className="flex flex-col gap-2.5 px-3 py-2.5"
      data-testid="prebill-entry"
    >
      <button
        type="button"
        onClick={onOpen}
        className="-mx-1.5 -my-1 grid grid-cols-[minmax(0,1fr)_46px_80px] items-start gap-x-3 gap-y-1 rounded-md px-1.5 py-1 text-left hover:bg-fill-1 @xl:grid-cols-[150px_minmax(0,1fr)_46px_92px_84px]"
      >
        <span className="flex min-w-0 items-center gap-1.5">
          <Initials name={entry.user_name || "?"} />
          <span className="flex min-w-0 flex-col">
            <span className="truncate text-base">{entry.user_name}</span>
            <span className="text-sm text-fg-3 tabular-nums">
              {shortDate(entry.work_date)}
            </span>
          </span>
        </span>
        <span className="col-span-3 row-start-2 text-base leading-4.75 @xl:col-span-1 @xl:row-start-auto">
          {entry.narrative || <span className="text-fg-3">No narrative</span>}
          {tag ? (
            <StatusBadge tone="success" className="ml-1.5">
              <Check /> {tag}
            </StatusBadge>
          ) : null}
        </span>
        <span className="text-right text-base font-medium text-fg tabular-nums">
          {formatHours(entry.duration_hours)}
        </span>
        <span
          className={cn(
            "text-right text-base tabular-nums",
            flat && "text-fg-3"
          )}
        >
          {flat ? "Flat fee" : formatAmount(entry.amount)}
        </span>
        <span className="hidden text-right text-sm text-fg-3 @xl:block">
          {entry.status === "approved" ? "Approved" : "Released"}
        </span>
      </button>
      {flags.map((flag) => (
        <div key={flag.id} className="@xl:ml-40.5">
          <FlagCard entry={entry} flag={flag} onReturn={() => onReturn(flag)} />
        </div>
      ))}
    </div>
  );
};

const RuleRow: FC<{ individualId: string; rule: AppliedRule }> = ({
  individualId,
  rule
}) => (
  <span className="flex items-center gap-2.5 py-1.5" data-testid="prebill-rule">
    <span
      className={cn("flex-1 text-base leading-4.5", !rule.on && "text-fg-3")}
      title={rule.quote ?? undefined}
    >
      {ruleTitle(rule)}
    </span>
    <Switch
      aria-label={`Check "${rule.label}" on this case`}
      checked={rule.on}
      disabled={!rule.enabled}
      onCheckedChange={(next) =>
        void ruleActions.setCaseRule(individualId, rule, next)
      }
    />
  </span>
);

const RulesPanel: FC<{ matter: PrebillMatter }> = ({ matter }) => {
  const fromClient = matter.rules.filter((r) => r.source === "guideline");
  const firm = matter.rules.filter((r) => r.source === "firm");
  const owner = matter.billed_to.company_name ?? matter.client_label;
  const addLink = matter.billed_to.company_id
    ? companyPath(matter.billed_to.company_id, "billing")
    : matter.foreign_national_id
    ? `/clients/${matter.foreign_national_id}?tab=settings`
    : null;
  return (
    <div
      className="flex min-h-0 flex-col gap-3.5 overflow-y-auto border-l border-line-2 px-4 py-3"
      data-testid="prebill-rules"
    >
      <span className="text-sm font-medium text-fg-3">Billing Guidelines</span>
      {matter.guidelines.length ? (
        matter.guidelines.map((g) => (
          <div
            key={g.id}
            className="flex items-start gap-2.5 rounded-xl bg-elevated px-3 py-2.5 ring-1 ring-line-2"
          >
            <FileText className="mt-0.5 size-4 shrink-0 text-danger" />
            <span className="flex min-w-0 flex-col gap-0.5">
              <span className="truncate text-base text-fg">
                {g.title || g.filename}
              </span>
              <span className="text-sm text-fg-3">
                {owner} · {g.rule_count} {g.rule_count === 1 ? "rule" : "rules"}{" "}
                read from the file
              </span>
            </span>
          </div>
        ))
      ) : (
        <div className="flex flex-col gap-2 rounded-xl border border-dashed border-line-3 p-3">
          <span className="text-sm leading-4.5 text-fg-3">
            No client guidelines on file for {owner}. Only firm rules are
            checked.
          </span>
          {addLink ? (
            <Button
              asChild
              variant="secondary"
              size="xs"
              className="self-start"
            >
              <Link to={addLink}>
                <Plus /> Add Guidelines
              </Link>
            </Button>
          ) : null}
        </div>
      )}
      {fromClient.length ? (
        <div className="flex flex-col">
          <span className="pb-1 text-sm font-medium text-fg-3">
            From the Client
          </span>
          {fromClient.map((r) => (
            <RuleRow
              key={`${r.source_id}:${r.id}`}
              individualId={matter.individual_id}
              rule={r}
            />
          ))}
        </div>
      ) : null}
      <div className="flex flex-col">
        <span className="pb-1 text-sm font-medium text-fg-3">Firm Rules</span>
        {firm.length ? (
          firm.map((r) => (
            <RuleRow key={r.id} individualId={matter.individual_id} rule={r} />
          ))
        ) : (
          <span className="text-sm text-fg-3">
            No firm rules yet. Add them in Time &amp; billing settings.
          </span>
        )}
      </div>
      <span className="flex gap-1.5 text-sm leading-4.25 text-fg-3">
        <Info className="mt-0.5 size-3.5 shrink-0" />
        Rules flag entries before they're approved. Nothing changes until you
        apply a fix or return the entry.
      </span>
    </div>
  );
};

const MatterView: FC<{
  matter: PrebillMatter;
  onOpenAll: () => void;
}> = ({ matter, onOpenAll }) => {
  const [flaggedOnly, setFlaggedOnly] = useState(false);
  const [userId, setUserId] = useState<string | null>(null);
  const [opened, setOpened] = useState<TimeEntry | null>(null);
  const [returning, setReturning] = useState<TimeEntry | null>(null);
  const returnShown = useHeld(returning);
  const [approving, setApproving] = useState(false);
  const flat = isFlat(matter.fee_arrangement);
  const shown = filterEntries(matter.entries, { flaggedOnly, userId });
  const toApprove = matter.released;
  const blocked = matter.entries.some(isBlocking);
  const note = blockingNote(matter);
  const people = Object.entries(matter.people);

  return (
    <div className="flex min-h-0 min-w-0 flex-col gap-3 overflow-y-auto px-5 py-4">
      <div className="flex items-start gap-3">
        <span className="flex min-w-0 flex-1 flex-col gap-1">
          <span className="flex items-center gap-2">
            <span className="truncate text-lg font-medium text-fg">
              {matter.label}
            </span>
            <Tag className="capitalize">
              {flat ? "Flat fee" : matter.fee_arrangement.replace(/_/g, " ")}
            </Tag>
          </span>
          <span className="text-sm text-fg-3 tabular-nums">
            {countsLine(
              { ...matter, entries: matter.entries.length },
              matter.fee_arrangement,
              matter.flat_fee
            )}{" "}
            · Billed to {matter.billed_to.label}
          </span>
        </span>
        <span
          title={
            blocked
              ? "Fix, dismiss or return the flagged entries first"
              : undefined
          }
        >
          <Button
            variant="primary"
            size="sm"
            disabled={!toApprove || blocked || approving}
            onClick={async () => {
              setApproving(true);
              try {
                await prebillActions.approveMatter(matter);
              } finally {
                setApproving(false);
              }
            }}
            data-testid="approve-matter"
          >
            <CircleCheck />
            {toApprove ? `Approve Matter · ${toApprove}` : "Approved"}
          </Button>
        </span>
      </div>
      {note ? (
        <span className="flex items-center gap-2 text-sm text-danger">
          <AlertTriangle className="size-3.5" /> {note}
        </span>
      ) : null}

      <span className="flex items-center gap-1.5">
        {flaggedOnly || matter.entries.some((e) => openFlags(e).length) ? (
          <Button
            variant={flaggedOnly ? "tonal" : "secondary"}
            size="xs"
            aria-pressed={flaggedOnly}
            onClick={() => setFlaggedOnly((v) => !v)}
          >
            Flagged Only
          </Button>
        ) : null}
        {people.length > 1 ? (
          <Select
            value={userId ?? "all"}
            onValueChange={(v) => setUserId(v === "all" ? null : v)}
          >
            <SelectTrigger size="sm" aria-label="Timekeeper" className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Every Timekeeper</SelectItem>
              {people.map(([id, name]) => (
                <SelectItem key={id} value={id}>
                  {name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : null}
        <span className="flex-1" />
        {shown.length < matter.entries.length ? (
          <span className="text-sm text-fg-3 tabular-nums">
            {shown.length} of {matter.entries.length} entries
          </span>
        ) : null}
      </span>

      <div className="@container shrink-0 divide-y divide-line-1 overflow-hidden rounded-xl bg-elevated ring-1 ring-line-2">
        {shown.length ? (
          shown.map((entry) => (
            <EntryRow
              key={entry.id}
              entry={entry}
              flat={flat}
              onOpen={() => setOpened(entry)}
              onReturn={(flag) =>
                void prebillActions.returnEntry(flag, entry.user_name)
              }
            />
          ))
        ) : (
          <span className="block p-3.5 text-base text-fg-3">
            No entries match these filters.
          </span>
        )}
      </div>
      <button
        type="button"
        onClick={onOpenAll}
        className="-ml-1 inline-flex items-center gap-1 self-start rounded-md px-1.5 py-1 text-sm text-fg-3 transition-colors hover:bg-fill-1 hover:text-fg"
      >
        See Every Entry in All Entries <ArrowRight className="size-3" />
      </button>

      <ReviewEntrySheet
        row={opened}
        onClose={() => setOpened(null)}
        onReturn={(entry) => {
          setOpened(null);
          setReturning(entry);
        }}
      />
      <ReturnDialog
        open={Boolean(returning)}
        count={1}
        to={returnShown ? firstName(returnShown.user_name) : null}
        onOpenChange={(open) => !open && setReturning(null)}
        onReturn={async (text) => {
          if (returning)
            await reviewActions.returnEntries([returning.id], text);
        }}
      />
    </div>
  );
};

export const PrebillTab: FC<{ range: Range; onOpenAll: () => void }> = ({
  range,
  onOpenAll
}) => {
  const matters = usePrebill(range);
  const rows = useMemo(() => matters.data ?? [], [matters.data]);
  const [picked, setPicked] = useState<string | null>(null);
  const selected =
    rows.find((m) => m.individual_id === picked)?.individual_id ??
    rows[0]?.individual_id ??
    null;
  const matter = usePrebillMatter(selected);

  useEffect(() => {
    if (selected !== picked) setPicked(selected);
  }, [selected, picked]);

  return (
    <div className="flex min-h-0 flex-1 flex-col" data-testid="prebill-tab">
      <WaitingStrip range={range} />
      {matters.isLoading ? (
        <Skeleton className="m-4 h-40" />
      ) : !rows.length ? (
        <EmptyState
          icon={Clock}
          title="Nothing Released Yet"
          description="Nothing released for these dates yet."
        />
      ) : (
        <div className="grid min-h-0 flex-1 grid-cols-[260px_minmax(0,1fr)_312px]">
          <MatterList rows={rows} selected={selected} onPick={setPicked} />
          {matter.data ? (
            <>
              <MatterView
                key={matter.data.individual_id}
                matter={matter.data}
                onOpenAll={onOpenAll}
              />
              <RulesPanel matter={matter.data} />
            </>
          ) : (
            <>
              <Skeleton className="m-4 h-40" />
              <span />
            </>
          )}
        </div>
      )}
    </div>
  );
};
